import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCourses, getTeachers, getTestimonials, getResults, submitApplication } from '../api';
import { useLang } from '../context/LangContext';
import { tCourse, tTeacher, tExperience, tAgeRange, tCoursesSubtitle } from '../contentI18n';
import { useSEO } from '../hooks/useSEO';
import Dropdown from '../components/Dropdown';
import TeacherPhoto from '../components/TeacherPhoto';
import './Home.css';

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}
function AnimSection({ children, className = '' }) {
  const [ref, inView] = useInView();
  return <div ref={ref} className={`anim-section ${inView ? 'visible' : ''} ${className}`}>{children}</div>;
}

// Subtle 3D tilt on hover — restrained (max ~5deg), disabled for reduced-motion users and on touch.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function TiltCard({ children, className, onClick, style, maxDeg = 5 }) {
  const ref = useRef(null);
  const onMouseMove = (e) => {
    if (prefersReducedMotion() || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ref.current.style.transform = `perspective(900px) rotateX(${(-py * maxDeg).toFixed(2)}deg) rotateY(${(px * maxDeg).toFixed(2)}deg) translateY(-4px)`;
  };
  const onMouseLeave = () => { if (ref.current) ref.current.style.transform = ''; };
  return (
    <div ref={ref} className={className} style={style} onClick={onClick} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
      {children}
    </div>
  );
}

function CertificateCard({ band, side }) {
  return (
    <div className={`hero-cert hero-cert-${side}`} aria-hidden="true">
      <svg viewBox="0 0 200 150" className="hero-cert-svg" role="img">
        {/* Card body, drawn with a slightly uneven outline so it reads as a sketch */}
        <path
          d="M12 16 Q10 8 19 7 L181 5 Q190 6 189 15 L191 132 Q192 141 183 142 L20 144 Q11 143 12 134 Z"
          fill="#FFFFFF"
          stroke="#1B3A6B"
          strokeWidth="3.5"
          strokeLinejoin="round"
        />
        {/* Header strip */}
        <path d="M12 16 Q10 8 19 7 L181 5 Q190 6 189 15 L189 36 L12 38 Z" fill="#1B3A6B" />
        <text x="24" y="28" className="hero-cert-label">IELTS</text>
        {/* Band score — the whole point of the card */}
        <text x="24" y="88" className="hero-cert-band">{band}</text>
        <text x="24" y="106" className="hero-cert-sub">band score</text>
        {/* Squiggles standing in for body text */}
        <path d="M24 118 q14 -4 28 0 t28 0" stroke="#CBD5E1" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M24 128 q10 -4 20 0 t20 0" stroke="#CBD5E1" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Cartoon seal */}
        <g transform="translate(150 96)">
          <circle r="24" fill="#F5820A" stroke="#1B3A6B" strokeWidth="3" />
          <path d="M-6 26 l-4 18 10 -7 10 7 -4 -18" fill="#F5820A" stroke="#1B3A6B" strokeWidth="3" strokeLinejoin="round" />
          <path d="M0 -12 l3.6 7.8 8.4 1 -6.2 5.9 1.6 8.5 -7.4 -4.2 -7.4 4.2 1.6 -8.5 -6.2 -5.9 8.4 -1 Z" fill="#FFFFFF" />
        </g>
        {/* Sparkles */}
        <path d="M170 26 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 Z" fill="#FF9B35" />
        <path d="M120 66 l1.5 3.6 3.6 1.5 -3.6 1.5 -1.5 3.6 -1.5 -3.6 -3.6 -1.5 3.6 -1.5 Z" fill="#F5820A" />
      </svg>
    </div>
  );
}

function HeroWords({ text, className = '', startDelay = 0 }) {
  const words = String(text).split(' ').filter(Boolean);
  return (
    <span className={`hero-line ${className}`}>
      {words.map((word, i) => (
        <span
          key={`${word}-${i}`}
          className="hero-word"
          style={{ animationDelay: `${(startDelay + i * 0.09).toFixed(2)}s` }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}

const catColor = { English:'#2563EB', Math:'#059669', Russian:'#DC2626', Uzbek:'#7C3AED', German:'#1A1A1A', Spanish:'#D97706' };

const emptyForm = { name: '', phone: '', age: '', telegram: '', course: '', format: '', days: '', time: '', message: '', purpose: 'trial' };

export default function Home() {
  const { t, lang } = useLang();
  useSEO(t('hero_title1') + ' ' + t('hero_title2'), t('hero_subtitle'));
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formStatus, setFormStatus] = useState('');
  const [activeTest, setActiveTest] = useState(0);
  const navigate = useNavigate();
  const heroRef = useRef(null);

  useEffect(() => {
    getCourses().then(setCourses).catch(() => {});
    getTeachers().then(setTeachers).catch(() => {});
    getTestimonials().then(setTestimonials).catch(() => {});
    getResults().then(setResults).catch(() => {});
  }, []);

  // The records arrive in English; translate them once here so the cards, the
  // profile modal and the course dropdown all read in one language. The English
  // title is kept alongside as `enTitle`, because that is the value the
  // application form submits and the admin panel expects to see.
  const localisedCourses = courses.map(c => ({ ...tCourse(c, lang), enTitle: c.title }));
  const localisedTeachers = teachers.map(x => tTeacher(x, lang));
  const openTeacher = selectedTeacher ? tTeacher(selectedTeacher, lang) : null;

  const scrollToApply = (purpose) => {
    setFormData((f) => ({ ...f, purpose }));
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Subtle hero parallax — a few px of depth on mouse move, off for reduced-motion.
  const onHeroMouseMove = (e) => {
    if (prefersReducedMotion() || !heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    heroRef.current.style.setProperty('--parallax-x', `${(px * 18).toFixed(1)}px`);
    heroRef.current.style.setProperty('--parallax-y', `${(py * 18).toFixed(1)}px`);
  };

  useEffect(() => {
    if (!testimonials.length) return;
    const ti = setInterval(() => setActiveTest(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(ti);
  }, [testimonials.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.course) { setFormStatus('error'); return; }
    try { await submitApplication(formData); setFormStatus('success'); setFormData(emptyForm); }
    catch { setFormStatus('error'); }
  };

  const WHY = [
    { icon: '👨‍🏫', title: t('why1_title'), desc: t('why1_desc') },
    { icon: '🎯', title: t('why2_title'), desc: t('why2_desc') },
    { icon: '💡', title: t('why3_title'), desc: t('why3_desc') },
    { icon: '🧭', title: t('why4_title'), desc: t('why4_desc') },
    { icon: '📊', title: t('why5_title'), desc: t('why5_desc') },
    { icon: '🤝', title: t('why6_title'), desc: t('why6_desc') },
    { icon: '📱', title: t('why7_title'), desc: t('why7_desc') },
  ];
  const TRIAL = [1, 2, 3, 4, 5].map((n) => t(`trial${n}`));
  // The last two figures are counted, never typed: they come from the very
  // arrays the sections below render, so the hero cannot claim a course count
  // the catalogue does not show. Add a filter to the cards and it has to be
  // applied here too — compute it once and feed both.
  // Same sentence, same source as the stat and the cards — one count, stated once.
  const coursesSubtitle = tCoursesSubtitle(courses.length, t, lang);
  const STATS = [
    { num: '600+', label: t('stat1') },
    { num: '96%', label: t('stat2') },
    { num: teachers.length ? `${teachers.length}` : '—', label: t('stat3') },
    { num: courses.length ? `${courses.length}` : '—', label: t('stat4') },
  ];

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero" ref={heroRef} onMouseMove={onHeroMouseMove}>
        <div className="hero-bg">
          <div className="hero-blob blob1" />
          <div className="hero-blob blob2" />
          <div className="hero-blob blob3" />
          <div className="hero-grid" />
        </div>
        <div className="container hero-content">
          <div className="hero-badge animate-fadeUp">✦ {t('hero_badge')}</div>
          <CertificateCard band="8.0" side="right" />
          <h1 className="hero-title hero-title-3d notranslate" translate="no">
            <HeroWords text={t('hero_title1')} startDelay={0.12} />
            <HeroWords text={t('hero_title2')} className="hero-accent" startDelay={0.34} />
          </h1>
          <p className="hero-subtitle animate-fadeUp" style={{ animationDelay: '0.2s' }}>{t('hero_subtitle')}</p>
          <div className="hero-actions animate-fadeUp" style={{ animationDelay: '0.3s' }}>
            <button className="btn-primary" onClick={() => scrollToApply('trial')}>{t('hero_btn1')}</button>
            <button className="btn-outline-white" onClick={() => scrollToApply('level_check')}>{t('hero_btn2')}</button>
          </div>
          <div className="hero-stats animate-fadeUp" style={{ animationDelay: '0.4s' }}>
            {STATS.map(s => (
              <div key={s.label} className="stat-item">
                <span className="stat-num">{s.num}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-scroll-dot" />
      </section>

      {/* COURSES */}
      <section className="section bg-white">
        <div className="container">
          <AnimSection><div className="section-header"><span className="section-tag">{t('what_we_teach')}</span><h2>{t('our_courses')}</h2><p>{coursesSubtitle}</p></div></AnimSection>
          <div className="courses-grid">
            {localisedCourses.map((c) => (
              <AnimSection key={c.id}>
                <TiltCard className="course-card" onClick={() => navigate(`/courses/${c.id}`)} style={{ '--cat': catColor[c.category] || '#2563EB' }}>
                  <div className="course-card-top">
                    <span className="course-icon-big">{c.icon}</span>
                    <span className="course-cat-badge" style={{ color: catColor[c.category], background: `${catColor[c.category]}15` }}>{c.categoryLabel}</span>
                  </div>
                  <h3 className="course-title">{c.title}</h3>
                  <p className="course-desc">{c.description.substring(0, 95)}...</p>
                  {c.note && <div className="course-note">📌 {c.note}</div>}
                  <div className="course-meta">
                    <span>⏱ {c.duration}</span>
                    <span>📚 {c.levels}</span>
                  </div>
                  <div className="course-cta">{t('learn_more')}</div>
                </TiltCard>
              </AnimSection>
            ))}
          </div>
          <div className="section-cta"><Link to="/courses" className="btn-blue">{t('view_all')}</Link></div>
        </div>
      </section>

      {/* TEACHERS */}
      <section className="section bg-offwhite">
        <div className="container">
          <AnimSection><div className="section-header"><span className="section-tag blue">{t('who_teaches')}</span><h2>{t('meet_teachers')}</h2><p>{t('teachers_subtitle')}</p></div></AnimSection>
          <div className="teachers-grid-home">
            {localisedTeachers.slice(0, 4).map(teacher => (
              <AnimSection key={teacher.id}>
                <TiltCard className="teacher-home-card" onClick={() => setSelectedTeacher(teacher)} maxDeg={4}>
                  <div className="thc-img-wrap">
                    <TeacherPhoto src={teacher.photo} name={teacher.name} position={teacher.photo_position} className="thc-img" />
                  </div>
                  <div className="thc-body">
                    <h3>{teacher.name}</h3>
                    <p className="thc-subject">{teacher.subject}</p>
                    {teacher.experience && (
                      <p className="thc-exp">🏆 {tExperience(teacher.experience, t, lang)}</p>
                    )}
                    {teacher.age_min > 0 && (
                      <p className="thc-age">👤 {tAgeRange(teacher.age_min, teacher.age_max, t, lang)}</p>
                    )}
                    <p className="thc-bio">{teacher.short_bio}</p>
                    <span className="thc-link">{t('view_profile')}</span>
                  </div>
                </TiltCard>
              </AnimSection>
            ))}
          </div>
          <div className="section-cta"><Link to="/teachers" className="btn-outline-navy">{t('all_teachers')}</Link></div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section bg-navy" id="why-us">
        <div className="container">
          <AnimSection><div className="section-header light"><span className="section-tag">{t('our_advantage')}</span><h2>{t('why_choose')}</h2><p>{t('why_subtitle')}</p></div></AnimSection>
          <div className="why-grid">
            {WHY.map((item, i) => (
              <AnimSection key={i}>
                <div className="why-card">
                  <div className="why-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — mini teaser, full detail on /how-it-works */}
      <section className="section bg-white">
        <div className="container">
          <AnimSection><div className="section-header"><span className="section-tag">{t('hiw_tag')}</span><h2>{t('hiw_title')}</h2></div></AnimSection>
          <div className="hiw-teaser-steps">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className="hiw-teaser-step">
                <span className="hiw-teaser-num">{n}</span>
                <p>{t(`hiw_step${n}`)}</p>
              </div>
            ))}
          </div>
          <div className="section-cta"><Link to="/how-it-works" className="btn-outline-navy">{t('view_how')}</Link></div>
        </div>
      </section>

      {/* RESULTS — only shown once real entries exist (Cabinet → Catalogue → Results) */}
      {results.length > 0 && (
        <section className="section bg-offwhite">
          <div className="container">
            <AnimSection><div className="section-header"><span className="section-tag">{t('results_tag')}</span><h2>{t('results_title')}</h2><p>{t('results_subtitle')}</p></div></AnimSection>
            <div className="results-grid">
              {results.map(r => (
                <AnimSection key={r.id}>
                  <div className="result-card">
                    <div className="result-course">{r.course}</div>
                    <div className="result-value">{r.result}</div>
                    <div className="result-name">{r.name}{r.date ? ` · ${r.date}` : ''}</div>
                  </div>
                </AnimSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <AnimSection><div className="section-header"><span className="section-tag">{t('student_stories')}</span><h2>{t('what_students_say')}</h2></div></AnimSection>
            <div className="testimonial-showcase">
              <div className="testimonial-quote">"</div>
              <p className="testimonial-text">{testimonials[activeTest].text}</p>
              <div className="testimonial-author">
                <div className="testimonial-av">{testimonials[activeTest].avatar}</div>
                <div>
                  <div className="testimonial-name">{testimonials[activeTest].name}</div>
                  <div className="testimonial-meta">{testimonials[activeTest].course} · {testimonials[activeTest].score}</div>
                </div>
              </div>
              <div className="testimonial-stars">{'★'.repeat(testimonials[activeTest].rating)}</div>
              <div className="testimonial-dots">
                {testimonials.map((_, i) => <button key={i} className={`dot ${i === activeTest ? 'active' : ''}`} onClick={() => setActiveTest(i)} />)}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ — mini teaser, full list on /faq */}
      <section className="section bg-navy">
        <div className="container">
          <AnimSection><div className="section-header light"><span className="section-tag">{t('faq_tag')}</span><h2>{t('faq_title')}</h2></div></AnimSection>
          <div className="faq-teaser-grid">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className="faq-teaser-item">
                <strong>{t(`faq_q${n}`)}</strong>
                <p>{t(`faq_a${n}`)}</p>
              </div>
            ))}
          </div>
          <div className="section-cta"><Link to="/faq" className="btn-outline-white">{t('view_faq')}</Link></div>
        </div>
      </section>

      {/* TRIAL LESSON — what the brief asks us to spell out before the form */}
      <section className="section bg-white">
        <div className="container">
          <AnimSection>
            <div className="section-header">
              <span className="section-tag">{t('trial_tag')}</span>
              <h2>{t('trial_title')}</h2>
              <p>{t('trial_subtitle')}</p>
            </div>
          </AnimSection>
          <div className="trial-grid">
            {TRIAL.map((item, i) => (
              <AnimSection key={i}>
                <div className="trial-card">
                  <span className="trial-num">{String(i + 1).padStart(2, '0')}</span>
                  <p>{item}</p>
                </div>
              </AnimSection>
            ))}
          </div>
          <div className="section-cta">
            {/* The first lesson takes a name and a number and nothing else —
                worth saying next to the button, where somebody is deciding
                whether this is about to turn into a sign-up form. */}
            <p className="trial-no-signup">{t('trial_no_signup')}</p>
            <button className="btn-primary" onClick={() => scrollToApply('trial')}>{t('hero_btn1')}</button>
          </div>
        </div>
      </section>

      {/* APPLY FORM */}
      <section className="section bg-offwhite" id="apply">
        <div className="container">
          <div className="apply-wrapper">
            <div className="apply-left">
              <span className="section-tag">{t('get_started')}</span>
              <h2>{t('ready_to')}</h2>
              <p>{formData.purpose === 'level_check' ? t('apply_subtitle_level') : t('apply_subtitle_trial')}</p>
              <div className="apply-perks">
                <div className="perk">✓ {t('perk1')}</div>
                <div className="perk">✓ {t('perk2')}</div>
                <div className="perk">✓ {t('perk3')}</div>
              </div>
            </div>
            <form className="apply-form" onSubmit={handleSubmit}>
              <div className="form-group"><label>{t('your_name')}</label><input type="text" placeholder={t('name_placeholder')} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div>
              <div className="apply-form-row">
                <div className="form-group"><label>{t('phone_number')}</label><input type="tel" placeholder={t('phone_placeholder')} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} /></div>
                <div className="form-group"><label>{t('your_age')}</label><input type="number" min="1" max="120" placeholder={t('age_placeholder')} value={formData.age} onChange={e => setFormData({ ...formData, age: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>{t('telegram_label')}</label><input type="text" placeholder={t('telegram_placeholder')} value={formData.telegram} onChange={e => setFormData({ ...formData, telegram: e.target.value })} /></div>
              <div className="form-group"><label>{t('select_course')}</label>
                <Dropdown
                  value={formData.course}
                  onChange={v => setFormData({ ...formData, course: v })}
                  placeholder={t('select_course')}
                  options={localisedCourses.map(c => ({ value: c.enTitle, label: c.title }))}
                />
              </div>
              <div className="form-group">
                <label>{t('format_label')}</label>
                <Dropdown
                  value={formData.format}
                  onChange={v => setFormData({ ...formData, format: v })}
                  placeholder={t('format_label')}
                  options={[
                    { value: 'group', label: t('format_group') },
                    { value: 'individual', label: t('format_individual') },
                  ]}
                />
              </div>
              <div className="apply-form-row">
                <div className="form-group"><label>{t('days_label')}</label><input type="text" placeholder={t('days_placeholder')} value={formData.days} onChange={e => setFormData({ ...formData, days: e.target.value })} /></div>
                <div className="form-group"><label>{t('time_label')}</label><input type="text" placeholder={t('time_placeholder')} value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>{t('message_opt')}</label><textarea placeholder={t('msg_placeholder')} rows={3} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} /></div>
              {formStatus === 'success' && <div className="form-success">{t('form_success')}</div>}
              {formStatus === 'error' && <div className="form-error">{t('form_error')}</div>}
              <button type="submit" className="btn-primary full-width">{t('send_app')}</button>
            </form>
          </div>
        </div>
      </section>

      {/* TEACHER MODAL */}
      {openTeacher && (
        <div className="modal-overlay" onClick={() => setSelectedTeacher(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedTeacher(null)}>✕</button>
            <div className="modal-teacher-header">
              <TeacherPhoto src={openTeacher.photo} name={openTeacher.name} position={openTeacher.photo_position} className="modal-teacher-photo" />
              <div>
                <h2>{openTeacher.name}</h2>
                <p className="modal-subject">{openTeacher.subject}</p>
                {openTeacher.experience && (
                  <p className="modal-exp">🏆 {tExperience(openTeacher.experience, t, lang)}</p>
                )}
                {openTeacher.age_min > 0 && (
                  <p className="modal-age">👤 {tAgeRange(openTeacher.age_min, openTeacher.age_max, t, lang)}</p>
                )}
                {openTeacher.education && (
                  <p className="modal-edu">🎓 {openTeacher.education}</p>
                )}
              </div>
            </div>
            <p className="modal-bio">{openTeacher.full_bio}</p>
            {openTeacher.certifications?.length > 0 && (
              <div className="modal-tags-section"><h4>{t('certifications')}</h4><div className="modal-tags">{openTeacher.certifications.map((c,i) => <span key={i} className="tag">{c}</span>)}</div></div>
            )}
            {/* Achievements stay hidden until verified ones are entered in the cabinet, under Catalogue → Teachers — see backend/ARCHITECTURE.md */}
            <button className="btn-primary" onClick={() => { setSelectedTeacher(null); document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }); }}>
              {t('enroll_with', { name: openTeacher.name.split(' ')[0] })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
